import { Pencil } from 'lucide-react'
import { SegmentedControl } from '@teach/ui'
import { QuotePlate } from '@/features/ai/components/QuotePlate'
import type { BlockEditQuoteView } from '@/features/ai/useBlockEditQuote'
import { isEditScopeAvailable } from './editScopeAvailability'
import type { useEditScope } from './useEditScope'
import { useMessages } from '@/i18n'
import { messages } from './sidebarMessages'

const SectionTitle = ({
  icon: Icon,
  children,
}: {
  icon: typeof Pencil
  children: React.ReactNode
}) => (
  <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-wider text-muted-foreground">
    <Icon className="h-3.5 w-3.5" />
    {children}
  </div>
)

/**
 * Область правки — по образцу `GenerationSettings` (та же обёртка со
 * скроллом, тот же `SectionTitle`, тот же `SegmentedControl fullWidth
 * size="sm"`, та же `QuotePlate`). Переключателя области в рефе нет — это
 * наш экран, поэтому стилизован по внутреннему образцу.
 *
 * `SegmentedControl` не умеет гасить один сегмент — недоступная область
 * поэтому не гаснет молча, а объясняется текстом рядом (константа CLAUDE.md
 * «никаких фальшивых аффордансов»): переключиться на неё можно, но экран
 * сразу говорит, почему в ней нечего править.
 */
export const EditBlockSettings = ({
  scope,
  quote,
}: {
  scope: ReturnType<typeof useEditScope>
  quote: BlockEditQuoteView
}) => {
  const m = useMessages(messages)
  const isAvailable = isEditScopeAvailable(scope)

  return (
    <div className="scrollbar-hide min-h-0 flex-1 overflow-y-auto overflow-x-hidden pb-4 pl-5 pr-7 pt-2">
      <div className="space-y-6 px-1">
        <div className="space-y-2">
          <SectionTitle icon={Pencil}>{m.editScope}</SectionTitle>
          <SegmentedControl
            fullWidth
            size="sm"
            options={[
              { id: 'block', label: m.scopeBlock },
              { id: 'lesson', label: m.scopeWholeLesson },
              // count — размер НАБОРА мультивыбора (`selectionCount`), а не
              // размера активной области: виден до переключения на неё.
              { id: 'selection', label: m.scopeSelected, count: scope.selectionCount },
            ]}
            value={scope.scope}
            onChange={(id) => scope.setScope(id)}
          />
          {scope.isLoadingBlocks ? (
            // Пока ответа на блоки урока ещё нет, `hasBlock`/`hasLessonBlocks`
            // честно `false`, но это НЕ значит «блоков нет» — значит «мы пока
            // не знаем». Ложное «недоступно» хуже молчания (оно конкретно и
            // проверяемо врёт), поэтому здесь нейтральная строка, а не отказ.
            <p className="text-xs text-muted-foreground">{m.loadingLessonBlocks}</p>
          ) : (
            <>
              {scope.hasBlock ? null : (
                <p className="text-xs text-muted-foreground">{m.scopeBlockHint}</p>
              )}
              {scope.hasLessonBlocks ? null : (
                <p className="text-xs text-muted-foreground">{m.scopeLessonHint}</p>
              )}
              {scope.hasSelection ? null : (
                <p className="text-xs text-muted-foreground">{m.scopeSelectionHint}</p>
              )}
              {isAvailable ? (
                <>
                  <p className="text-xs text-muted-foreground">{m.blocksInEdit(scope.count)}</p>
                  <QuotePlate
                    price={quote.price}
                    balance={quote.balance}
                    isLoading={quote.isLoading}
                  />
                </>
              ) : null}
            </>
          )}
          {scope.hasCustomBadgeIcon ? (
            <p className="text-xs text-warning">{m.customBadgeWarning}</p>
          ) : null}
        </div>
      </div>
    </div>
  )
}
